import React, { useState } from 'react';

import { Formik, FieldArray } from 'formik';
import * as Yup from 'yup';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import TextFormField from 'CommonComponents/TextFormField';

/**
 * Label for a quantity field in the Edit Expense modal (IB-927).
 *
 * "Qty" — the term the revenue commit screens use — for the first quantity, which is the only one
 * most expenses have. An expense may be counted in several units at once and they multiply (the
 * expense screen shows this as "Days x Items"), so the second and any further field is numbered:
 * "Qty (2)", "Qty (3)". The numbering is what tells the fields apart, and each slot is individually
 * meaningful — quantity links are held against amount_slot_index. Dave's call, 12 Aug.
 */
const quantityLabel = (index) => (index === 0 ? $t('Qty') : $t('Qty ({{n}})', { n: index + 1 }));

const getValidationSchema = (initialAmounts, isProjectSblExpense) => {
    const schema = {
        price: Yup.number().required($t('This value is required')),
        amounts: Yup.array().of(
            Yup.object().shape({
                amount: Yup.number()
                    .transform((value) => (isNaN(value) ? undefined : value))
                    .required($t('This value is required'))
                    .test('max-amount', $t('Cannot exceed the initial amount'), function (value) {
                        if (!isProjectSblExpense) return true;
                        const numericValue = parseFloat(value || 0);
                        const maxAmount = parseFloat(initialAmounts[0]?.amount || 0);
                        return numericValue <= maxAmount;
                    }),
            }),
        ),
    };

    return Yup.object().shape(schema);
};

export default function UncommittedExpendituresEditForm(props) {
    const [isLoading, setIsLoading] = useState(false);
    const [isProceedAllowed, setIsProceedAllowed] = useState(false);
    const [allowProceedToCommit, setAllowProceedToCommit] = useState(true);

    const initialAmounts =
        props.uncommittedExpenditure.amountObject.length > 0
            ? props.uncommittedExpenditure.amountObject
            : props.uncommittedExpenditure.expense.uncommitted_amount
              ? props.uncommittedExpenditure.expense.uncommitted_amount
              : props.uncommittedExpenditure.expense.amount;

    const submitForm = (values, actions) => {
        axios
            .put(props.uncommittedExpendituresUpdateUrl.replace('UNCOMMITTED_EXPENDITURE_ID', props.uncommittedExpenditure.id), values)
            .then((response) => {
                props.successHandler(response.data);
                setIsLoading(false);
            })
            .catch((e) => {
                setIsLoading(false);
                actions.setErrors(e.response.data.errors);
                // A line with a budget change pending approval refuses the commitment, saying why.
                if (e.response?.data?.errors?.pending_line_change) {
                    alert(e.response.data.errors.pending_line_change[0]);
                }
            });
    };

    return (
        <Formik
            initialValues={{
                price:
                    'price' in props.uncommittedExpenditure.amount_data
                        ? props.uncommittedExpenditure.amount_data.price
                        : (props.uncommittedExpenditure.expense.price ?? 0.0),
                amounts:
                    props.uncommittedExpenditure.amountObject.length > 0
                        ? props.uncommittedExpenditure.amountObject
                        : props.uncommittedExpenditure.expense.uncommitted_amount
                          ? props.uncommittedExpenditure.expense.uncommitted_amount
                          : props.uncommittedExpenditure.expense.amount,
            }}
            // validationSchema={CommittedExpendituresEditSchema}
            validationSchema={getValidationSchema(initialAmounts, props.isProjectSblExpense)}
            onSubmit={(values, actions) => {
                setIsLoading(true);
                if (isProceedAllowed) {
                    submitForm(values, actions);
                } else {
                    if (confirm($t('Are you sure you want to proceed as it might change original budget!'))) {
                        axios
                            .post(props.uncommittedExpendituresPreCommitUrl.replace('UNCOMMITTED_EXPENDITURE_ID', props.uncommittedExpenditure.id), values)
                            .then((response) => {
                                setAllowProceedToCommit(response.data.allowProceedToCommit);
                                setIsLoading(false);
                                if (response.data.allowProceedToCommit) {
                                    if (response.data.displayCommitDetailsTable) {
                                        props.displayConfirmProceed(response.data);
                                        setIsProceedAllowed(true);
                                    } else {
                                        setIsLoading(true);
                                        submitForm(values, actions);
                                    }
                                }
                            })
                            .catch((e) => {
                                setIsLoading(false);
                                console.log(e);
                            });
                    }
                }
            }}
        >
            {({ errors, touched, handleSubmit, values }) => (
                <>
                    {!isProceedAllowed && (
                        <>
                            {/* formatted (NumberFormat) rather than type="number": a number input
                                cannot carry thousands separators, so these were the only unseparated
                                figures on the Direct Commitment flow. */}
                            <div className="tw-grid tw-grid-cols-1">
                                {props.isProjectSblExpense ? (
                                    <TextFormField
                                        label={$t('Price')}
                                        required={true}
                                        formatted
                                        thousandSeparator={true}
                                        decimalScale={2}
                                        inputMode="decimal"
                                        name="price"
                                        placeholder="Price"
                                        disabled
                                    ></TextFormField>
                                ) : (
                                    <TextFormField
                                        label={$t('Price')}
                                        required={true}
                                        formatted
                                        thousandSeparator={true}
                                        decimalScale={2}
                                        inputMode="decimal"
                                        name="price"
                                        placeholder="Price"
                                    ></TextFormField>
                                )}
                            </div>
                            <FieldArray
                                name="amounts"
                                render={(arrayHelpers) => (
                                    <>
                                        {values.amounts.map((amount, index) => (
                                            <div key={index} className="tw-grid tw-grid-cols-1">
                                                <TextFormField
                                                    // IB-927: "Qty" is the shared term with the revenue commit screens, so a
                                                    // single-quantity expense — the usual case — reads simply "Qty".
                                                    // An expense may carry more than one quantity unit, and they multiply
                                                    // (the expense screen renders that as "Days x Items"). There the unit is
                                                    // the only thing telling the fields apart, and the slots are
                                                    // individually meaningful, so it is appended: "Qty (Days)", "Qty (Items)".
                                                    label={quantityLabel(index)}
                                                    required={true}
                                                    formatted
                                                    thousandSeparator={true}
                                                    decimalScale={2}
                                                    inputMode="decimal"
                                                    name={`amounts.${index}.amount`}
                                                    placeholder={quantityLabel(index)}
                                                    error={errors.amounts?.[index]?.amount}
                                                    touched={(touched.amounts?.[index]?.amount || false).toString()}
                                                ></TextFormField>
                                            </div>
                                        ))}
                                    </>
                                )}
                            />
                        </>
                    )}

                    {!allowProceedToCommit && (
                        <Alert className="tw-my-2" type="warning">
                            {$t('There is insufficient amount available to fund the increase in budget requested.')}
                        </Alert>
                    )}

                    <div className="modal__footer">
                        <Button variant="primary" disabled={isLoading} onClick={handleSubmit}>
                            {isProceedAllowed ? $t('Proceed') : $t('Update')}
                        </Button>
                    </div>
                </>
            )}
        </Formik>
    );
}
